import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { BusinessContentProvider } from './contexts/BusinessContentContext';
import '@/App.css';

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import AIGenerator from './pages/AIGenerator';
import LeadsEnhanced from './pages/LeadsEnhanced';
import Campaigns from './pages/Campaigns';
import Enrollments from './pages/Enrollments';
import Funnels from './pages/Funnels';
import Automations from './pages/Automations';
import Inbox from './pages/Inbox';
import Analytics from './pages/Analytics';
import Billing from './pages/Billing';
import DocAnalyzer from './pages/DocAnalyzer';
import DocumentHistory from './pages/DocumentHistory';
import Team from './pages/Team';
import AdminPanel from './pages/AdminPanel';
import ABTesting from './pages/ABTesting';
import CustomDomains from './pages/CustomDomains';
import SocialMedia from './pages/SocialMedia';
import PublicDocView from './pages/PublicDocView';
import AdvancedAnalytics from './pages/AdvancedAnalytics';
import LeadPriorityQueue from './pages/LeadPriorityQueue';
import EmailTemplates from './pages/EmailTemplates';
import WhatsAppIntegration from './pages/WhatsAppIntegration';
import VoiceAISettings from './pages/VoiceAISettings';
import VoiceAIDashboard from './pages/VoiceAIDashboard';
import WhiteLabelSettings from './pages/WhiteLabelSettings';
import PageBuilder from './pages/PageBuilder';
import LeadCapture from './pages/LeadCapture';
import ABTestingEmails from './pages/ABTestingEmails';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Refund from './pages/Refund';
import SMS from './pages/SMS';
import Layout from './components/Layout';
import OnboardingWizard from './components/OnboardingWizard';
import WelcomeTour from './components/WelcomeTour';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminClientDetail from './pages/SuperAdminClientDetail';
import SuperAdminRevenue from './pages/SuperAdminRevenue';
import SuperAdminHealth from './pages/SuperAdminHealth';
import SuperAdminBroadcast from './pages/SuperAdminBroadcast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const queryClient = new QueryClient();

function App() {
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  useEffect(() => {
    // Check for token in URL (from OAuth redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken) {
      localStorage.setItem('auth_token', urlToken);
      setToken(urlToken);
      window.history.replaceState({}, document.title, '/');
    }
  }, []);

  // Check onboarding status when logged in
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!token) {
        setCheckingOnboarding(false);
        return;
      }
      
      // Check local storage first as a quick check
      const onboardingShown = localStorage.getItem('onboarding_shown');
      if (onboardingShown === 'true') {
        setShowOnboarding(false);
        setCheckingOnboarding(false);
        return;
      }
      
      try {
        const res = await fetch(`${API_URL}/api/auth/workspace-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (!data.onboarding_complete) {
            setShowOnboarding(true);
          } else {
            // Mark as shown in localStorage too
            localStorage.setItem('onboarding_shown', 'true');
          }
        }
      } catch (error) {
        console.error('Failed to check onboarding:', error);
      } finally {
        setCheckingOnboarding(false);
      }
    };
    
    checkOnboarding();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('onboarding_shown');
    setToken(null);
  };

  const handleOnboardingComplete = () => {
    localStorage.setItem('onboarding_shown', 'true');
    setShowOnboarding(false);
    // Show welcome tour after onboarding
    const tourCompleted = localStorage.getItem('welcome_tour_completed');
    if (tourCompleted !== 'true') {
      setShowWelcomeTour(true);
    }
    // Dispatch event to refresh business content
    window.dispatchEvent(new CustomEvent('businessTypeChanged'));
  };

  const handleOnboardingSkip = () => {
    localStorage.setItem('onboarding_shown', 'true');
    setShowOnboarding(false);
    // Show welcome tour after skip too
    const tourCompleted = localStorage.getItem('welcome_tour_completed');
    if (tourCompleted !== 'true') {
      setShowWelcomeTour(true);
    }
  };

  const handleWelcomeTourComplete = () => {
    setShowWelcomeTour(false);
  };

  const handleTourNavigate = (path) => {
    // Navigation will be handled by the Layout component
    window.location.href = path;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <BusinessContentProvider>
        <BrowserRouter>
          <div className="App">
            <Toaster position="top-right" />
            
            {/* Onboarding Wizard */}
            {token && showOnboarding && !checkingOnboarding && (
              <OnboardingWizard 
                onComplete={handleOnboardingComplete}
                onSkip={handleOnboardingSkip}
              />
            )}
            
            {/* Welcome Tour - shows after onboarding */}
            {token && showWelcomeTour && !showOnboarding && (
              <WelcomeTour 
                onComplete={handleWelcomeTourComplete}
                onNavigate={handleTourNavigate}
              />
            )}
            
            <Routes>
              <Route path="/" element={!token ? <LandingPage /> : <Navigate to="/dashboard" />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/doc/:token" element={<PublicDocView />} />
              <Route
                path="/*"
                element={
                  token ? (
                    <Layout onLogout={handleLogout}>
                      <Routes>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/ai-generator" element={<AIGenerator />} />
                        <Route path="/leads" element={<LeadsEnhanced />} />
                        <Route path="/lead-priority" element={<LeadPriorityQueue />} />
                        <Route path="/campaigns" element={<Campaigns />} />
                        <Route path="/email-templates" element={<EmailTemplates />} />
                        <Route path="/enrollments" element={<Enrollments />} />
                        <Route path="/funnels" element={<Funnels />} />
                        <Route path="/automations" element={<Automations />} />
                        <Route path="/inbox" element={<Inbox />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/advanced-analytics" element={<AdvancedAnalytics />} />
                        <Route path="/billing" element={<Billing />} />
                        <Route path="/doc-analyzer" element={<DocAnalyzer />} />
                        <Route path="/doc-history" element={<DocumentHistory />} />
                        <Route path="/team" element={<Team />} />
                        <Route path="/admin" element={<AdminPanel />} />
                        <Route path="/ab-testing" element={<ABTesting />} />
                        <Route path="/domains" element={<CustomDomains />} />
                        <Route path="/social" element={<SocialMedia />} />
                        <Route path="/whatsapp" element={<WhatsAppIntegration />} />
                        <Route path="/sms" element={<SMS />} />
                        <Route path="/voice-ai" element={<VoiceAISettings />} />
                        <Route path="/voice-ai-dashboard" element={<VoiceAIDashboard />} />
                        <Route path="/white-label" element={<WhiteLabelSettings />} />
                        <Route path="/page-builder" element={<PageBuilder />} />
                        <Route path="/lead-capture" element={<LeadCapture />} />
                        <Route path="/ab-testing-emails" element={<ABTestingEmails />} />
                        {/* Super Admin Routes */}
                        <Route path="/super-admin" element={<SuperAdminDashboard />} />
                        <Route path="/super-admin/client/:clientId" element={<SuperAdminClientDetail />} />
                        <Route path="/super-admin/revenue" element={<SuperAdminRevenue />} />
                        <Route path="/super-admin/health" element={<SuperAdminHealth />} />
                        <Route path="/super-admin/broadcast" element={<SuperAdminBroadcast />} />
                      </Routes>
                    </Layout>
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
            </Routes>
          </div>
        </BrowserRouter>
      </BusinessContentProvider>
    </QueryClientProvider>
  );
}

export default App;
